import React, { useEffect, useState } from "react";
import {
  ELightPath,
  PlateTypeCaptureProfile,
} from "../../Model/CaptureProfile";
import { getOneImagerCapabilities } from "../../redux/reducers/ImagerCapabilitiesReducer";
import { useTypedSelector } from "../../redux/reducers/RootReducer";
import FocusLevelOptions from "./DefaultImagingSettingViews/FocusLevelOptions";
import NonVisibleDropLocation from "./DefaultImagingSettingViews/NonVisibleDropLocation";
import OpticsAndExposure from "./DefaultImagingSettingViews/OpticsAndExposure";
import VisibleDropLocation from "./DefaultImagingSettingViews/VisibleDropLocation";
import TabPanel from "./TabPanel";
import AccordionToggle from "../Plate/AccordionToggle";
import SwitchButton from "../Buttons/SwitchButton";
import LightPathUtil from "../../Utility/LightPathUtil";
import Collapse from "../Common/Collapse";
import Disabled from "../Controls/Disabled";
import ImagerManualControl from "../Imager/ImagerManualControl";
import CommandPanelComboBox from "../CommandPanel/CommandPanelComboBox";
import { PlateType } from "../../Model/PlateType";
import Modal from "../Common/Modal";
import WarningModalView from "./WarningModalView";
import MtiButton from "../Buttons/MtiButton";
import { useDispatch } from "react-redux";
import { sendNotification } from "../../redux/actions/NotificationAction";
import { ENotificationType } from "../../Model/ENotificationType";

enum EImagingSettingsTabState {
  OPTIC_EXPOSURE,
  DROP_LOCATION,
  FOCUS_LEVEL,
}

type Props = {
  imagerIdentifier: string;
  lightPaths: ELightPath[];
  captureProfiles: PlateTypeCaptureProfile[];
  setCaptureProfile: (captureProfile: PlateTypeCaptureProfile) => void;
  style: React.CSSProperties;
  onErrorOccur: (key: string) => void;
  onErrorCleared: (key: string) => void;
  wellLimitEnabled: boolean;
  plateType: PlateType;
  isSelectedPlateTypeInImager: boolean;
};

export default function DefaultImagingSettings({
  imagerIdentifier,
  lightPaths,
  captureProfiles,
  setCaptureProfile,
  style,
  onErrorOccur,
  onErrorCleared,
  wellLimitEnabled,
  plateType,
  isSelectedPlateTypeInImager,
}: Props) {
  const [lightPath, setLightpath] = useState(lightPaths[0]);
  const [showControlPanel, setShowControlPanel] = useState(false);
  const [selectedTabIndex, setSelectedTabIndex] = useState(0);

  const tabHeaders = ["General", "Drop Positions"];

  const headersCanBeClicked = new Map<string, boolean>();
  headersCanBeClicked.set("Imaging Settings", true);
  if (isSelectedPlateTypeInImager) {
    headersCanBeClicked.set("Control Panel", true);
  } else {
    headersCanBeClicked.set("Control Panel", false);
  }

  const OnClickTabHeader = (header: string) => {
    if (header === "Control Panel" && !isSelectedPlateTypeInImager) {
      setShowControlPanel(true);
    } else {
      setSelectedTabIndex(tabHeaders.findIndex((x) => x === header));
    }
  };

  useEffect(() => {
    if (!isSelectedPlateTypeInImager && selectedTabIndex !== 0) {
      setSelectedTabIndex(0);
    }
  }, [isSelectedPlateTypeInImager]);

  return (
    <>
      <TabPanel
        id="DefaultImagingSetting"
        collapsible={false}
        style={{ minHeight: 308, height: "100%", ...style }}
        disabled={false}
        allowChange={true}
        activeTabStyle={{ padding: 0 }}
        headersCanBeClicked={headersCanBeClicked}
        onClickTab={OnClickTabHeader}
        defaultSelectedIndex={selectedTabIndex}
      >
        {{
          "Imaging Settings": (
            <>
              <CommandPanelComboBox<number>
                disabled={lightPaths.length === 0}
                label="Lightpath"
                value={lightPath}
                onChange={(value) => setLightpath(value)}
                displayValues={lightPaths.map((lp) => ({
                  display: LightPathUtil.toName(lp),
                  value: lp,
                }))}
                style={{
                  paddingLeft: 19,
                  paddingRight: 11,
                  borderBottom: "1px solid white",
                }}
              />
              <DefaultLightPathImagingSettings
                lightPath={lightPath}
                imagerIdentifier={imagerIdentifier}
                captureProfiles={captureProfiles}
                setCaptureProfile={setCaptureProfile}
                onErrorOccur={onErrorOccur}
                onErrorCleared={onErrorCleared}
                style={style}
                plateType={plateType}
              />
            </>
          ),
          "Control Panel": (
            <ImagerManualControl
              wellLimitEnabled={wellLimitEnabled}
              imagerIdentifier={imagerIdentifier}
              headerEnabled={false}
              style={{
                overflow: "none",
                height: "auto",
              }}
            />
          ),
        }}
      </TabPanel>
      <Modal
        show={showControlPanel}
        hideOnBackdropClick={true}
        onHide={() => setShowControlPanel(false)}
        style={{
          width: 686,
          minHeight: 422,
        }}
      >
        <Modal.Body>
          <WarningModalView
            message="You are not allow to access Control Panel tab"
            subMessage="Please move your selected plate to the Imager before access Control Panel tab"
            onClickOk={() => setShowControlPanel(false)}
          />
        </Modal.Body>
        <Modal.Footer
          style={{
            display: "flex",
            justifyContent: "flex-end",
            alignItems: "center",
          }}
        >
          <MtiButton
            variant="primary"
            size="large"
            style={{
              width: 157,
            }}
            onClick={() => setShowControlPanel(false)}
          >
            OK
          </MtiButton>
        </Modal.Footer>
      </Modal>
    </>
  );
}

type LightPathProps = {
  imagerIdentifier: string;
  lightPath: ELightPath;
  captureProfiles: PlateTypeCaptureProfile[];
  setCaptureProfile: (captureProfile: PlateTypeCaptureProfile) => void;
  style: React.CSSProperties;
  onErrorOccur: (key: string) => void;
  onErrorCleared: (key: string) => void;
  plateType: PlateType;
};

function DefaultLightPathImagingSettings({
  imagerIdentifier,
  lightPath,
  captureProfiles,
  setCaptureProfile,
  style,
  onErrorOccur,
  onErrorCleared,
  plateType,
}: LightPathProps) {
  const DEF_IMG_SETTING_ACCORDION_STATE = "DEF_IMG_SETTING_ACCORDION_STATE";
  const [disableAllMenu, setDisableAllMenu] = useState(false);
  const dispatch = useDispatch();

  const imagerCapabilities = useTypedSelector((state) =>
    getOneImagerCapabilities(state, imagerIdentifier)
  );
  const lightPathCaptureprofile = captureProfiles.filter(
    (cp) => cp.lightpath === lightPath
  )[0];
  const visibleCaptureprofile = captureProfiles.filter(
    (cp) => cp.lightpath === ELightPath.Visible
  )[0];
  const otherCaptureProfiles = captureProfiles.filter(
    (cp) => cp.lightpath !== lightPath
  );
  const lightpathCapability =
    imagerCapabilities.lightpathCollection.byId[lightPath];
  const [imagingSettingsTabState, setImagingSettingsTabState] = useState(
    getAccordionState(DEF_IMG_SETTING_ACCORDION_STATE)
  );

  function getAccordionState(key: string): boolean[] {
    const value = localStorage.getItem(key);
    if (value != null) {
      return JSON.parse(value);
    } else {
      return [true, false, false];
    }
  }

  const setAccoordionState = (idx: number) => {
    let state = [false, false];

    state[idx] = !imagingSettingsTabState[idx];

    setImagingSettingsTabState(state);
  };

  useEffect(() => {
    localStorage.setItem(
      DEF_IMG_SETTING_ACCORDION_STATE,
      JSON.stringify(imagingSettingsTabState)
    );
  }, [imagingSettingsTabState]);

  useEffect(() => {
    if (lightpathCapability.lightpath === ELightPath.Visible) {
      if (
        lightpathCapability.optic.objectiveZoom !== null &&
        (lightPathCaptureprofile.visible?.objectiveZoom == null ||
          lightPathCaptureprofile.dropLocation?.missedDropZoom == null)
      ) {
        setDisableAllMenu(true);
        dispatch(
          sendNotification(
            ENotificationType.Error,
            "Error occured when load objective zoom and menu will be disabled, please see log for more information"
          )
        );
      }

      if (
        lightpathCapability.optic.continuousZoom !== null &&
        (lightPathCaptureprofile.visible?.continuousZoom == null ||
          lightPathCaptureprofile.dropLocation?.missedDropZoom == null)
      ) {
        setDisableAllMenu(true);
        dispatch(
          sendNotification(
            ENotificationType.Error,
            "Error occured when load continuous zoom and menu will be disabled, please see log for more information"
          )
        );
      }
    }

    if (lightPathCaptureprofile.lightpath === ELightPath.Ultraviolet) {
      if (
        lightpathCapability.optic.objectiveZoom !== null &&
        lightPathCaptureprofile.ultraviolet?.objectiveZoom == null
      ) {
        setDisableAllMenu(true);
        dispatch(
          sendNotification(
            ENotificationType.Error,
            "Error occured when load objective zoom and menu will be disabled, please see log for more information"
          )
        );
      }
    }
  }, [plateType]);

  return (
    <>
      {lightPath !== ELightPath.Visible && (
        <div
          style={{
            display: "flex",
            padding: "12px 29px 12px 23px",
            justifyContent: "space-between",
            alignItems: "center",
            fontSize: 14,
          }}
        >
          <div>Enable For This Plate Type</div>
          <div></div>
          <SwitchButton
            isOn={lightPathCaptureprofile.enabled}
            onToggle={() => {
              setCaptureProfile({
                ...lightPathCaptureprofile,
                enabled: !lightPathCaptureprofile.enabled,
              });
            }}
            disabled={disableAllMenu}
          />
        </div>
      )}
      <AccordionToggle
        expanded={
          imagingSettingsTabState[EImagingSettingsTabState.OPTIC_EXPOSURE]
        }
        onClick={() =>
          setAccoordionState(EImagingSettingsTabState.OPTIC_EXPOSURE)
        }
      >
        Optics &amp; Exposure
      </AccordionToggle>
      <Collapse
        expanded={
          imagingSettingsTabState[EImagingSettingsTabState.OPTIC_EXPOSURE]
        }
      >
        <Disabled disabled={!lightPathCaptureprofile.enabled || disableAllMenu}>
          <OpticsAndExposure
            captureProfile={lightPathCaptureprofile}
            setCaptureProfile={setCaptureProfile}
            lightPath={lightPath}
            lightpathCapability={lightpathCapability}
            onErrorOccur={onErrorOccur}
            onErrorCleared={onErrorCleared}
            style={{
              padding: "11px 18px 11px 27px",
            }}
          />
        </Disabled>
      </Collapse>
      <AccordionToggle
        expanded={
          imagingSettingsTabState[EImagingSettingsTabState.DROP_LOCATION]
        }
        onClick={() =>
          setAccoordionState(EImagingSettingsTabState.DROP_LOCATION)
        }
      >
        Drop Location
      </AccordionToggle>
      <Collapse
        expanded={
          imagingSettingsTabState[EImagingSettingsTabState.DROP_LOCATION]
        }
      >
        <Disabled disabled={!lightPathCaptureprofile.enabled || disableAllMenu}>
          {lightPath === ELightPath.Visible && (
            <VisibleDropLocation
              captureProfile={lightPathCaptureprofile}
              nonVisibleCaptureProfiles={otherCaptureProfiles}
              setCaptureProfile={setCaptureProfile}
              lightpathCapability={lightpathCapability}
              style={{
                padding: "11px 18px 11px 27px",
              }}
              onErrorOccur={onErrorOccur}
              onErrorCleared={onErrorCleared}
              plateType={plateType}
            />
          )}
          {lightPath !== ELightPath.Visible && (
            <NonVisibleDropLocation
              visibleCaptureProfile={visibleCaptureprofile}
              captureProfile={lightPathCaptureprofile}
              setCaptureProfile={setCaptureProfile}
              lightPath={lightPath}
              lightpathCapability={lightpathCapability}
              plateType={plateType}
              style={{
                padding: "11px 18px 11px 27px",
              }}
              onErrorOccur={onErrorOccur}
              onErrorCleared={onErrorCleared}
              setDisableAllMenu={setDisableAllMenu}
            />
          )}
        </Disabled>
      </Collapse>
      <AccordionToggle
        expanded={imagingSettingsTabState[EImagingSettingsTabState.FOCUS_LEVEL]}
        onClick={() => setAccoordionState(EImagingSettingsTabState.FOCUS_LEVEL)}
      >
        Focus Level
      </AccordionToggle>
      <Collapse
        expanded={imagingSettingsTabState[EImagingSettingsTabState.FOCUS_LEVEL]}
      >
        <Disabled disabled={!lightPathCaptureprofile.enabled || disableAllMenu}>
          <FocusLevelOptions
            lighPathCapability={lightpathCapability}
            imagerIdentifier={imagerIdentifier}
            captureProfile={lightPathCaptureprofile}
            otherCaptureProfiles={otherCaptureProfiles}
            setCaptureProfile={setCaptureProfile}
            lightPath={lightPath}
            style={{
              padding: "11px 18px 11px 27px",
            }}
            onErrorOccur={onErrorOccur}
            onErrorCleared={onErrorCleared}
          />
        </Disabled>
      </Collapse>
    </>
  );
}
